import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    effect,
    Injector,
    inject,
    input,
    output,
    untracked,
} from '@angular/core';
import { areRadioButtonStatesEqual, RADIO_BUTTON_INITIAL_STATE } from './cost-to-build-client-ui-radio-button.state';
import { RadioButtonStore } from './cost-to-build-client-ui-radio-button.store';
import type { RadioButtonState } from './cost-to-build-client-ui-radio-button.types';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    providers: [RadioButtonStore],
    selector: 'ctb-radio-button',
    styleUrl: './cost-to-build-client-ui-radio-button.scss',
    templateUrl: './cost-to-build-client-ui-radio-button.html',
})
export class CtbRadioButtonComponent {
    readonly #injector = inject(Injector);
    readonly #store = inject(RadioButtonStore);

    readonly ariaLabel = input<string>(RADIO_BUTTON_INITIAL_STATE.ariaLabel);
    readonly checked = input(RADIO_BUTTON_INITIAL_STATE.checked, { transform: booleanAttribute });
    readonly disabled = input(RADIO_BUTTON_INITIAL_STATE.disabled, { transform: booleanAttribute });
    readonly id = input<string>(RADIO_BUTTON_INITIAL_STATE.id);
    readonly label = input<string>(RADIO_BUTTON_INITIAL_STATE.label);
    readonly name = input<string>(RADIO_BUTTON_INITIAL_STATE.name);
    readonly value = input<string>(RADIO_BUTTON_INITIAL_STATE.value);

    readonly selected = output<string>();
    readonly stateChange = output<RadioButtonState>();

    readonly isDisabled = this.#store.isDisabled;
    readonly resolvedAriaLabel = this.#store.resolvedAriaLabel;
    readonly state = this.#store.state;

    #lastEmittedState: RadioButtonState | null = null;

    readonly syncStoreFromInputs = effect(() => this.#syncStoreFromInputs(), { injector: this.#injector });

    readonly emitStateChanges = effect(
        () => {
            const nextState = this.state();

            const previousState = this.#lastEmittedState;
            if (previousState !== null && areRadioButtonStatesEqual(previousState, nextState)) {
                return;
            }

            this.#lastEmittedState = nextState;
            this.stateChange.emit(nextState);
        },
        { injector: this.#injector }
    );

    onNativeChange() {
        if (this.isDisabled()) {
            return;
        }

        this.#store.setState({ checked: true });
        this.selected.emit(this.state().id);
    }

    #syncStoreFromInputs() {
        const patch: RadioButtonState = {
            ariaLabel: this.ariaLabel(),
            checked: this.checked(),
            disabled: this.disabled(),
            id: this.id(),
            label: this.label(),
            name: this.name(),
            value: this.value(),
        };

        untracked(() => this.#store.setState(patch));
    }
}
